import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, forkJoin, fromEvent, of, retry } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CommonIdPlaylist } from '../../../models/common-id/common-id-playlist';
import { Playlist, Theme, ThemeForDisplay } from '../../../models/playlist';
import {
  ReadableEnglishPlaylistProblem,
  ReadableEnglishProblem,
  ReadableHistoryPlaylistProblem,
  ReadableHistoryProblem,
  ReadableNationalLanguagePlaylistProblem,
  ReadableNationalLanguageProblem,
  ReadableProblem,
  ReadableSciencePlaylistProblem,
  ReadableScienceProblem
} from '../../../models/problem';
import { User } from '../../../models/user';
import { SCIENCE_IDS, SUBJECT_NAMES, SubjectId } from '../../../resources/config';
import { Dates } from '../../../utils/dates';
import { Log } from '../../../utils/log';
import { ProblemImageUtil } from '../../../utils/problem-image-util';
import { splitImageItemsByMixedLayout } from '../../../utils/mixed-layout-util';
import { University } from '../../../models/common-data';
import { Store } from '@ngrx/store';
import { RootState } from '../../../reducers';
import { StaticCommonData } from 'src/app/models/static-common-data';
import { CommonIdSubjectUtil } from 'src/app/utils/common-id/common-id-subject-util';

interface ProblemImage {
  url: string;
  width: number;
  height: number;
}

interface ImageItem {
  readableProblemId: string;
  fImage?: ProblemImage;
  problemImages?: ProblemImage[];
  layoutType?: number;
  headerLabel?: string;
}

@Component({
  selector: 'app-problem-detail-frame-for-print',
  templateUrl: './problem-detail-frame-for-print.component.html',
  styleUrls: ['./problem-detail-frame-for-print.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProblemDetailFrameForPrintComponent implements OnChanges {
  @Input() menuType: 'article' | 'spellcheck' = 'article';
  @Input() universities: University[];
  @Input() staticCommonData: StaticCommonData;
  @Input() readableEnglishProblems: ReadableEnglishProblem[];
  @Input() readableScienceProblems: ReadableScienceProblem[];
  @Input() readableNationalLanguageProblems: ReadableNationalLanguageProblem[];
  @Input() readableJapaneseHistoryProblems: ReadableHistoryProblem[];
  @Input() readableWorldHistoryProblems: ReadableHistoryProblem[];
  @Input() englishPlaylistTheme: ThemeForDisplay<ReadableEnglishPlaylistProblem>;
  @Input() sciencePlaylistTheme: ThemeForDisplay<ReadableSciencePlaylistProblem>;
  @Input() nationalLanguagePlaylistTheme: ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>;
  @Input() japaneseHistoryPlaylistTheme: ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  @Input() worldHistoryPlaylistTheme: ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  @Input() playlist: Playlist | CommonIdPlaylist;
  @Input() theme: Theme;
  @Input() paperMode: boolean;
  @Input() trialMode: boolean;
  @Input() subjectId: string;
  // 頭紙フッタ部分のテキストをBtoS、BtoCで切り替えるためのフラグ
  @Input() isBToC: boolean;
  // BtoSの場合のみ必要
  @Input() signedInUser: User;

  // お気に入りリストの印刷で使用する変数
  @Input() isFromBookmark = false;
  @Input() isAutoPrintDialog = false;
  @Input() bookmarkListName = '';

  // おすすめ問題かどうかを判断するフラグ
  @Input() isFromPlaylist = false;

  @Output() dataReadyForPrint = new EventEmitter<void>();

  playlistTheme:
    | ThemeForDisplay<ReadableEnglishPlaylistProblem>
    | ThemeForDisplay<ReadableSciencePlaylistProblem>
    | ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>
    | ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  readablePlaylistProblem:
    | ReadableEnglishPlaylistProblem
    | ReadableSciencePlaylistProblem
    | ReadableNationalLanguagePlaylistProblem
    | ReadableHistoryPlaylistProblem;
  problemId: string;
  year: string;
  layoutType: number;
  readableProblem: ReadableProblem; // お気に入り以外の印刷で使用する変数
  readableProblems: ReadableProblem[]; // お気に入りの印刷で使用する変数
  subjectName: string;
  examDate: string;
  nyushiName: string;
  examTime: string;
  specialInfoData: string[];
  fImage: ProblemImage;
  problemImages: ProblemImage[];
  daimonId: string | null;
  daimonName: string | null;
  seriesName: string;
  headerCoverLabel: string;
  headerProblemLabel: string;
  originalSources: string[];
  outputDate: string;
  menuLabel: string;

  // お気に入りリストの印刷で使用する変数
  imageItems$: Observable<ImageItem[]> = of([]);
  imageItems: ImageItem[] = [];

  private LOG_SOURCE = this.constructor.name;

  constructor(private store: Store<RootState>, private activatedRoute: ActivatedRoute, private detector: ChangeDetectorRef) {}

  async ngOnChanges(): Promise<void> {
    this.imageItems = [];
    this.setUpReadableProblemBySubjectId();
    if (this.isFromPlaylist) this.setUpPlaylistThemeBySubjectId();
    this.setUpDataForCoverPage();

    if (this.playlist) {
      // BtoCのおすすめ問題詳細ではシリーズ名が入る
      this.seriesName = (this.playlist as CommonIdPlaylist).seriesName;
    }

    // 全問題に対して非同期処理
    const tasks = this.readableProblems.map(problem => {
      if (problem.questionLayout !== undefined && problem.answerLayout !== undefined) {
        return this.setUpDataForEachProblemAsync(problem);
      }
    });
    await Promise.all(tasks);

    // レイアウト混在 paper のみ daimon 単位に分割し、画像名 _l\d から layoutType を割り当てる（混在無しは noop）
    // split により items が daimon 単位になり .print-page (page-break-after: always) で大問改ページが自動的に効く
    this.imageItems = splitImageItemsByMixedLayout(this.imageItems, this.menuType);

    this.imageItems$ = of(this.imageItems);

    this.dataReadyForPrint.emit();

    this.detector.detectChanges();
  }

  calcImageLayout1(layoutType: number, images: ProblemImage[]): Array<Array<ProblemImage[]>> {
    const layoutImages: Array<Array<ProblemImage[]>> = [];
    let pages: Array<ProblemImage[]> = [];
    let containers: ProblemImage[] = [];

    if (images === undefined) {
      return layoutImages;
    }

    const maxHeight = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-content-h'))
        .trim()
        .replace('px', '')
    );
    const maxWidth = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-type-1-max-w'))
        .trim()
        .replace('px', '')
    );
    const gapY = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-type-1-gap-y'))
        .trim()
        .replace('px', '')
    );

    let height = 0;
    let daimon = '';
    images.map((image, index) => {
      const currentDaimon: string = this.getDaimonId(image.url);
      if (daimon === '') daimon = currentDaimon;

      // aspect ratio
      if (image.width > maxWidth) {
        image.height = image.height * (maxWidth / image.width);
        image.width = maxWidth;
      }

      // aspect ratio
      if (image.height > maxHeight) {
        image.width = image.width * (maxHeight / image.height);
        image.height = maxHeight;
      }

      if (height + image.height > maxHeight) {
        pages.push(containers);
        layoutImages.push(pages);
        containers = [];
        pages = [];
        height = 0;
      } else if (daimon !== currentDaimon) {
        daimon = currentDaimon;

        pages.push(containers);
        containers = [];
        height += gapY;
      }

      height += image.height;
      containers.push(image);

      if (index === images.length - 1) {
        if (containers.length > 0) pages.push(containers);
        if (pages.length > 0) layoutImages.push(pages);
      }
    });

    Log.debug(this.LOG_SOURCE, `calcImageLayout1:`, layoutImages);
    return layoutImages;
  }

  calcImageLayout2(layoutType: number, images: ProblemImage[]): Array<Array<ProblemImage[]>> {
    const layoutImages: Array<Array<ProblemImage[]>> = [];
    let pages: Array<ProblemImage[]> = [];
    let containers: ProblemImage[] = [];

    if (images === undefined) {
      return layoutImages;
    }

    const maxWidth = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-type-2-max-w'))
        .trim()
        .replace('px', '')
    );
    const maxHeight = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-type-2-max-h'))
        .trim()
        .replace('px', '')
    );
    const offSetTop = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-type-2-offset-top'))
        .trim()
        .replace('px', '')
    );
    const gapX = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-type-2-gap-x'))
        .trim()
        .replace('px', '')
    );
    const gapY = Number(
      String(getComputedStyle(document.querySelector('.problem-detail-frame-for-print')).getPropertyValue('--pd-print-type-2-gap-y'))
        .trim()
        .replace('px', '')
    );

    let width = 0;
    let daimon = '';
    images.map((image, index) => {
      const currentDaimon: string = this.getDaimonId(image.url);
      if (daimon === '') daimon = currentDaimon;

      // aspect ratio
      if (image.height > maxHeight) {
        image.width = image.width * (maxHeight / image.height);
        image.height = maxHeight;
      }

      // aspect ratio
      if (image.width > maxWidth) {
        image.height = image.height * (maxWidth / image.width);
        image.width = maxWidth;
      }

      if (width + image.width > maxWidth) {
        pages.push(containers);
        if (pages.length === 2) {
          layoutImages.push(pages);
          pages = [];
        }
        containers = [];
        width = 0;
      } else if (daimon !== currentDaimon) {
        daimon = currentDaimon;

        pages.push(containers);
        if (pages.length === 2) {
          layoutImages.push(pages);
          pages = [];
        }
        containers = [];
        width = 0;
      }

      width += image.width + gapX;
      containers.push(image);

      if (index === images.length - 1) {
        if (containers.length > 0) pages.push(containers);
        if (pages.length > 0) layoutImages.push(pages);
      }
    });

    Log.debug(this.LOG_SOURCE, `calcImageLayout2:`, layoutImages);
    return layoutImages;
  }

  calcImageLayout3(layoutType: number, images: ProblemImage[]): Array<Array<ProblemImage[]>> {
    const layoutImages: Array<Array<ProblemImage[]>> = [];
    let pages: Array<ProblemImage[]> = [];
    let containers: ProblemImage[] = [];

    if (images === undefined) {
      return layoutImages;
    }

    images.map((image, index) => {
      containers.push(image);

      if (containers.length === 2) {
        pages.push(containers);
        containers = [];

        if (pages.length === 2) {
          layoutImages.push(pages);
          pages = [];
        }
      }

      if (index === images.length - 1) {
        if (containers.length > 0) pages.push(containers);
        if (pages.length > 0) layoutImages.push(pages);
      }
    });

    Log.debug(this.LOG_SOURCE, `calcImageLayout3:`, layoutImages);
    return layoutImages;
  }

  private getDaimonId(url: string): string {
    // 掲載見合わせ画像の場合、大問IDがないので、ない場合には乱数で返す
    const daimonId: string | undefined = url.match('.+/(.+?).[a-z]+([?#;].*)?$')[1].split('_')[1];
    return daimonId
      ? daimonId
      : Math.random()
          .toString(32)
          .substring(2);
  }

  private getImage(imageUrl: string): Observable<ImageData | Error> {
    return new Observable<any>(observer => {
      const image = new Image();
      fromEvent(image, 'load')
        .pipe(take(1))
        .subscribe(e => {
          Log.debug(this.LOG_SOURCE, `load success:`, e.target);

          observer.next(e.target);
          observer.complete();
        });
      fromEvent(image, 'error')
        .pipe(take(1))
        .subscribe(error => {
          Log.debug(this.LOG_SOURCE, `load error:`, error);

          observer.error(error);
          image.remove();
        });
      image.src = imageUrl;
    });
  }

  private setUpReadableProblemBySubjectId() {
    if (SCIENCE_IDS.includes(this.subjectId)) {
      this.readableProblem = this.readableScienceProblems[0];
      this.readableProblems = this.readableScienceProblems;
    } else {
      const map = {
        [SubjectId.ENGLISH]: this.readableEnglishProblems,
        [SubjectId.NATIONAL_LANGUAGE]: this.readableNationalLanguageProblems,
        [SubjectId.JAPANESE_HISTORY]: this.readableJapaneseHistoryProblems,
        [SubjectId.WORLD_HISTORY]: this.readableWorldHistoryProblems
      };

      const problems = map[this.subjectId as keyof typeof map];
      if (problems) {
        this.readableProblem = problems[0]; // お気に入り以外の印刷で使用
        this.readableProblems = problems; // お気に入りリストの印刷で使用
      }
    }

    if (!this.readableProblem) return;
  }

  private setUpPlaylistThemeBySubjectId() {
    // URLより該当の問題IDを取得
    this.problemId = this.activatedRoute.snapshot.paramMap.get('problemId') || '';

    // 科目ごとのテーマをマッピング
    const themeMap = {
      [SubjectId.ENGLISH]: this.englishPlaylistTheme,
      [SubjectId.NATIONAL_LANGUAGE]: this.nationalLanguagePlaylistTheme,
      [SubjectId.JAPANESE_HISTORY]: this.japaneseHistoryPlaylistTheme,
      [SubjectId.WORLD_HISTORY]: this.worldHistoryPlaylistTheme
    };

    // 理科系は SCIENCE_IDS でまとめる
    if (SCIENCE_IDS.includes(this.subjectId)) {
      this.playlistTheme = this.sciencePlaylistTheme;
    } else {
      this.playlistTheme = themeMap[this.subjectId as keyof typeof themeMap] || null;
    }

    // 問題を検索
    this.readablePlaylistProblem = this.playlistTheme ? this.playlistTheme.problems.find(p => p.id === this.problemId) : null;
  }

  private setUpDataForCoverPage() {
    this.menuLabel = this.menuType === 'article' ? '問題' : '研究・解答';
    this.outputDate = Dates.simple4YmdStringFromIso(Dates.now());
  }

  private async setUpDataForEachProblemAsync(readableProblem: ReadableProblem): Promise<void> {
    this.problemId = readableProblem.id;
    this.year = `20${this.problemId.slice(0, 2)}`;
    this.layoutType = this.menuType === 'article' ? readableProblem.questionLayout.layoutType : readableProblem.answerLayout.layoutType;

    this.imageItems.push({ readableProblemId: readableProblem.id, layoutType: this.layoutType });

    if (this.trialMode) {
      this.daimonId = !this.paperMode ? '01' : null;
    } else {
      this.daimonId = !this.paperMode ? this.problemId.slice(-2) : null;
    }
    this.daimonName = !this.paperMode ? this.problemId.slice(-1) : null;

    const examPaper = readableProblem.examPaper;

    const paperId = this.problemId.slice(0, -2);
    const headerSubjectName = CommonIdSubjectUtil.getSubjectName(paperId, this.staticCommonData.subjects);

    if (readableProblem.isOriginalProblem) {
      this.originalSources = readableProblem.originalSource.match(/^[0-9]+:/)
        ? readableProblem.originalSource.split(':').map(description => {
            const year = description.slice(0, 2);
            const universityId = description.slice(2, 6);
            const universityName = this.universities.find(univ => univ.id === `D${universityId}`)?.name ?? '';
            let problemNumber = description.slice(10, 12);
            if (problemNumber.startsWith('0')) {
              problemNumber = problemNumber.slice(1);
            }
            return `20${year}年度 ${universityName} 大問${problemNumber}`;
          })
        : [readableProblem.originalSource];
      this.subjectName = headerSubjectName; // 表紙のタイトル部分の科目表示
      this.headerCoverLabel = `${headerSubjectName} ${readableProblem.originalOverview} ${this.menuLabel}`;
    } else {
      if (this.isFromBookmark) {
        this.subjectName = SUBJECT_NAMES[this.subjectId]; // 表紙のタイトル部分の科目表示
        this.headerCoverLabel = `${this.bookmarkListName}`;
        this.headerProblemLabel = `${this.year}年 ${headerSubjectName} ${readableProblem?.university} ${this.menuLabel}`;
      } else {
        this.subjectName = headerSubjectName; // 表紙のタイトル部分の科目表示
        this.headerCoverLabel = `${this.year}年 ${headerSubjectName} ${readableProblem?.university} ${this.menuLabel}`;
      }
    }

    const index = this.imageItems.findIndex(item => item.readableProblemId === readableProblem.id);
    if (index !== -1) {
      this.imageItems[index].headerLabel = this.headerProblemLabel;
    }

    this.examDate = examPaper && examPaper.examDate ? examPaper.examDate : '-';
    this.nyushiName = examPaper && examPaper.nyushiName ? examPaper.nyushiName : '-';
    this.examTime = examPaper && examPaper.examTime ? examPaper.examTime : '-';
    this.specialInfoData = [
      examPaper && examPaper.bikou ? `〔備考〕${examPaper.bikou}` : '',
      examPaper && examPaper.nyushiKamoku ? `〔入試科目〕${examPaper.nyushiKamoku}` : '',
      examPaper && examPaper.chuui ? `〔注意〕${examPaper.chuui}` : ''
    ].filter(Boolean);

    // fImage の読み込み（必要なら）
    if (!this.trialMode && this.paperMode && examPaper?.chkFlg === '1' && readableProblem.fImage !== '') {
      const fImageUrl = environment.cloudfrontHost + ProblemImageUtil.getPath(readableProblem.fImage, this.trialMode);
      const image = await this.getImage(fImageUrl)
        .pipe(
          retry(3),
          take(1),
          catchError(() => of(null))
        )
        .toPromise();
      if (image && !(image instanceof Error)) {
        const isRetina = fImageUrl.includes('@2x');
        this.fImage = {
          width: isRetina ? image.width / 2 : image.width,
          height: isRetina ? image.height / 2 : image.height,
          url: fImageUrl
        };
        if (index !== -1) this.imageItems[index].fImage = this.fImage;
      }
    }

    await this.setUpProblemImagesAsync(readableProblem);
  }

  private async setUpProblemImagesAsync(readableProblem: ReadableProblem): Promise<void> {
    return new Promise<void>(resolve => {
      const targetImages = this.menuType === 'article' ? readableProblem.questionImages : readableProblem.answerImages;
      if (!targetImages || targetImages.length === 0) {
        resolve();
        return;
      }

      const imageUrls = targetImages.map(img => environment.cloudfrontHost + ProblemImageUtil.getPath(img, this.trialMode));
      const filteredUrls = this.isFromBookmark
        ? imageUrls
        : imageUrls.filter(url => this.daimonId === null || url.includes((this.menuType === 'article' ? 'q' : 'a') + this.daimonId));
      const requests$ = filteredUrls.map(url =>
        this.getImage(url).pipe(
          retry(3),
          take(1),
          catchError(() => of(null))
        )
      );
      forkJoin(requests$).subscribe(images => {
        this.problemImages = images
          .map((image, i) => {
            if (!image || image instanceof Error) return null;
            const isRetina = filteredUrls[i].includes('@2x');
            return {
              width: isRetina ? image.width / 2 : image.width,
              height: isRetina ? image.height / 2 : image.height,
              url: filteredUrls[i]
            };
          })
          .filter(Boolean) as ProblemImage[];

        if (this.isFromBookmark && this.trialMode) {
          const grouped = this.trialProblemImagesGroupedByDaimonId(this.menuType);
          const readableProblemId = Number(this.imageItems[0].readableProblemId);
          const layoutType = this.imageItems[0].layoutType;
          this.imageItems = grouped.map((images, index) => ({
            readableProblemId: String(readableProblemId + index),
            layoutType,
            problemImages: images
          }));
        } else {
          const index = this.imageItems.findIndex(item => item.readableProblemId === readableProblem.id);
          if (index !== -1) this.imageItems[index].problemImages = this.problemImages;
        }

        resolve();
      });
    });
  }

  private trialProblemImagesGroupedByDaimonId(menuType: 'article' | 'spellcheck'): ProblemImage[][] {
    // 体験版の画像情報を、問題IDごとの配列にする
    return Object.values(
      this.problemImages.reduce((acc, item) => {
        const match = menuType === 'article' ? item.url.match(/_q\d{2}_/) : item.url.match(/_a\d{2}_/); // captures "_q01_", "_q02_", etc.
        const key = match ? match[0] : 'unknown';

        if (!acc[key]) acc[key] = [];
        acc[key].push(item);

        return acc;
      }, {} as ProblemImage)
    );
  }
}
